import React, { useState } from 'react';
import { X, Clock, Ban, CheckCircle, XCircle, Car, Calendar } from 'lucide-react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';

const VehicleHistoryModal = ({ isOpen, onClose, vehicleId, vehicleName }) => {
  const [activeTab, setActiveTab] = useState('block');

  const { data: blockHistory } = useQuery(
    ['vehicle-block-history', vehicleId],
    () => api.get(`/vehicles/${vehicleId}/block-history`),
    {
      enabled: isOpen && !!vehicleId && activeTab === 'block',
      select: (data) => data?.data?.data || data?.data
    }
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getActionIcon = (action) => {
    return action === 'blocked' ? <Ban className="h-4 w-4 text-red-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  const getBlockTypeBadge = (blockType) => {
    const typeConfig = {
      'temporary': { color: 'badge-warning', text: 'Temporary' },
      'permanent': { color: 'badge-danger', text: 'Permanent' }
    };
    
    const config = typeConfig[blockType] || { color: 'badge-secondary', text: blockType };
    return <span className={`badge ${config.color}`}>{config.text}</span>;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {vehicleName} - History
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b">
          <button
            onClick={() => setActiveTab('block')}
            className={`pb-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'block'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Ban className="h-4 w-4 inline mr-2" />
            Block History
          </button>
        </div>

        {activeTab === 'block' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Block History</h3>
            {blockHistory && blockHistory.length > 0 ? (
              <div className="space-y-3">
                {blockHistory.map((entry) => (
                  <div
                    key={entry._id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getActionIcon(entry.action)}
                        <span className="font-medium text-gray-900">
                          {entry.action === 'blocked' ? 'Blocked' : 'Unblocked'}
                        </span>
                        {entry.blockType && getBlockTypeBadge(entry.blockType)}
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatTime(entry.blockedOn)}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicle ID:</span>
                        <span className="text-gray-900">{entry.fleetSysId}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Registration:</span>
                        <span className="text-gray-900">{entry.regNo}</span>
                      </div>

                      {entry.blockReason && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reason:</span>
                          <span className="text-gray-900">{entry.blockReason}</span>
                        </div>
                      )}

                      {entry.blockExpiry && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Expiry:</span>
                          <span className="text-gray-900">{formatTime(entry.blockExpiry)}</span>
                        </div>
                      )}

                      {entry.blockedBy && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Blocked By:</span>
                          <span className="text-gray-900">
                            {entry.blockedBy?.name || entry.blockedBy?.email || 'Unknown'}
                          </span>
                        </div>
                      )}

                      {entry.unblockedBy && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Unblocked By:</span>
                          <span className="text-gray-900">
                            {entry.unblockedBy?.name || entry.unblockedBy?.email || 'Unknown'}
                          </span>
                        </div>
                      )}

                      {entry.unblockedOn && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Unblocked On:</span>
                          <span className="text-gray-900">{formatTime(entry.unblockedOn)}</span>
                        </div>
                      )}

                      {entry.notes && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Notes:</span>
                          <span className="text-gray-900">{entry.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Ban className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Block History</h3>
                <p className="text-gray-500">This vehicle has no block history.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleHistoryModal; 